import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { adminApi } from "../adminApi";

const label = { fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 };
const input = { width: "100%", height: 32, padding: "0 8px", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: 12.5, marginTop: 4 };
const btn = { background: "#1E3A5F", color: "white", border: "none", borderRadius: 6, padding: "7px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" };

export default function ExpertAdminRequestDetail({ requestId, onClose, onChanged }) {
  const [req, setReq] = useState(null);
  const [experts, setExperts] = useState([]);
  const [resources, setResources] = useState([]);
  const [selectedExpert, setSelectedExpert] = useState("");
  const [selectedResource, setSelectedResource] = useState("");
  const [notes, setNotes] = useState("");
  const [nextFollowUp, setNextFollowUp] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    const res = await adminApi.expertGetRequest(requestId);
    if (res.ok) {
      setReq(res.data.request);
      setNotes(res.data.request.internal_notes || "");
      setNextFollowUp((res.data.request.next_follow_up_at || "").slice(0, 10));
    }
  };
  useEffect(() => { load(); }, [requestId]); // eslint-disable-line
  useEffect(() => {
    adminApi.expertListExperts(true).then((r) => { if (r.ok) setExperts(r.data.experts || []); });
    adminApi.expertListResources(true).then((r) => { if (r.ok) setResources(r.data.resources || []); });
  }, []);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 2500); };
  const afterChange = async () => { await load(); onChanged(); };

  const setStatus = async (status) => {
    const res = await adminApi.expertPatchRequest(requestId, { status });
    if (res.ok) { flash("Status updated"); afterChange(); }
  };
  const saveNotesAndFollowUp = async () => {
    const patch = { internal_notes: notes, last_follow_up_at: new Date().toISOString() };
    if (nextFollowUp) patch.next_follow_up_at = new Date(nextFollowUp).toISOString();
    const res = await adminApi.expertPatchRequest(requestId, patch);
    if (res.ok) { flash("Follow-up saved"); afterChange(); }
  };
  const doAssign = async () => {
    if (!selectedExpert) return;
    const res = await adminApi.expertAssign(requestId, selectedExpert, "founder");
    if (res.ok) { flash("Expert assigned"); afterChange(); }
  };
  const doActivate = async () => {
    const res = await adminApi.expertActivate(requestId, 30);
    if (res.ok) { flash("30-day engagement started"); afterChange(); }
  };
  const doAttach = async () => {
    if (!selectedResource) return;
    const res = await adminApi.expertAttachResource(requestId, selectedResource);
    if (res.ok) { flash("Resource attached"); afterChange(); }
  };
  const doDetach = async (resourceId) => {
    const res = await adminApi.expertDetachResource(requestId, resourceId);
    if (res.ok) { flash("Resource removed"); afterChange(); }
  };

  if (!req) return null;

  return (
    <div data-testid="expert-request-detail" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 40, display: "flex", justifyContent: "flex-end" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 460, maxWidth: "100%", background: "#F9FAFB", height: "100%", overflowY: "auto", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0F1B2D" }}>{req.company_name || req.customer_name || "Request"}</div>
          <button data-testid="expert-detail-close" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
        </div>
        {msg && <div style={{ marginTop: 8, fontSize: 12, color: "#047857" }}>{msg}</div>}

        <div style={{ marginTop: 16 }}>
          <div style={label}>Request</div>
          <div style={{ fontSize: 13, color: "#0F1B2D", marginTop: 4, lineHeight: 1.6 }}>{req.request_text}</div>
        </div>
        {req.desired_outcome && (
          <div style={{ marginTop: 12 }}>
            <div style={label}>Desired outcome</div>
            <div style={{ fontSize: 13, color: "#0F1B2D", marginTop: 4 }}>{req.desired_outcome}</div>
          </div>
        )}

        <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <div style={label}>Status</div>
            <select data-testid="expert-detail-status" value={req.status} onChange={(e) => setStatus(e.target.value)} style={input}>
              {["new", "under_review", "assigned", "in_progress", "completed", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div style={label}>Priority</div>
            <select data-testid="expert-detail-priority" value={req.priority} onChange={(e) => adminApi.expertPatchRequest(requestId, { priority: e.target.value }).then(afterChange)} style={input}>
              {["normal", "important", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={label}>Assign C-ORE Expert</div>
          <select data-testid="expert-detail-assign-select" value={selectedExpert} onChange={(e) => setSelectedExpert(e.target.value)} style={input}>
            <option value="">Select expert…</option>
            {experts.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <button data-testid="expert-detail-assign-btn" onClick={doAssign} style={{ ...btn, marginTop: 6 }}>Assign Expert</button>
          {req.assigned_expert_id && <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 4 }}>Assigned: {req.assigned_at ? new Date(req.assigned_at).toLocaleDateString("en-IN") : "—"}</div>}
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={label}>30-day engagement</div>
          {req.engagement_started_at ? (
            <div style={{ fontSize: 12.5, color: "#0F1B2D", marginTop: 4 }}>
              {new Date(req.engagement_started_at).toLocaleDateString("en-IN")} – {new Date(req.engagement_ends_at).toLocaleDateString("en-IN")}
            </div>
          ) : (
            <button data-testid="expert-detail-activate-btn" onClick={doActivate} style={{ ...btn, marginTop: 6 }}>Start 30-day Engagement</button>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={label}>Next follow-up date</div>
          <input data-testid="expert-detail-next-followup" type="date" value={nextFollowUp} onChange={(e) => setNextFollowUp(e.target.value)} style={input} />
          <div style={{ ...label, marginTop: 10 }}>Internal notes</div>
          <textarea data-testid="expert-detail-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            style={{ ...input, height: "auto", padding: 8 }} />
          <button data-testid="expert-detail-save-followup" onClick={saveNotesAndFollowUp} style={{ ...btn, marginTop: 8 }}>Save follow-up</button>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={label}>Resources from C-ORE Expert</div>
          {(req.resources || []).map((r) => (
            <div key={r.id} data-testid={`expert-detail-resource-${r.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5, padding: "6px 0", borderBottom: "1px solid #E5E7EB" }}>
              <span>{r.title}</span>
              <button data-testid={`expert-detail-remove-resource-${r.id}`} onClick={() => doDetach(r.id)} style={{ background: "none", border: "none", color: "#DC2626", cursor: "pointer", fontSize: 11 }}>Remove</button>
            </div>
          ))}
          <select data-testid="expert-detail-resource-select" value={selectedResource} onChange={(e) => setSelectedResource(e.target.value)} style={{ ...input, marginTop: 8 }}>
            <option value="">Select resource…</option>
            {resources.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
          </select>
          <button data-testid="expert-detail-attach-btn" onClick={doAttach} style={{ ...btn, marginTop: 6 }}>Attach Resource</button>
        </div>

        {(req.updates || []).length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={label}>Customer updates</div>
            {req.updates.map((u) => (
              <div key={u.id} style={{ fontSize: 12.5, color: "#0F1B2D", padding: "6px 0", borderBottom: "1px solid #E5E7EB" }}>
                {new Date(u.created_at).toLocaleDateString("en-IN")} — {u.note}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
